import React, { FC, useState, useEffect, useMemo } from "react";
import { Col, Row, Card, Button } from "reactstrap";
import {
  Table,
  Input,
  Popover,
  Space,
  Tooltip,
  message,
  Checkbox,
  Form,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  TwitchOutlined,
  EditOutlined,
  BarChartOutlined,
  MessageOutlined,
  TrophyOutlined,
  SaveOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import StatusIndicatorDrawer from "./QuoteComparison/StatusIndicatorDrawer";
import ScenarioBuilderDrawer from "./QuoteComparison/ScenarioBuilderDrawer";
import AllocationDrawer from "./QuoteComparison/AllocationDrawer";
import ShortlistedSuppliersDrawer from "./QuoteComparison/ShortlistedSuppliersDrawer";
import quotationApis from "../../../api/master/quotations";
import SupplierDetailsDrawer from "./QuoteComparison/SupplierDetailsDrawer";
import Swal from "sweetalert2";
import AddSupplierQuoteDrawer from "./QuoteComparison/AddSupplierQuoteDrawer";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import miscService from "../../../api/misc/misc";
import PurchaseHistoryDrawer from "./QuoteComparison/PurchaseHistoryDrawer";
import PurchaseSummaryDrawer from "./QuoteComparison/PurchaseSummaryDrawer";
import { useSelector } from "react-redux";

dayjs.extend(utc);

//import { QuotationComparisonProps, SupplierQuotation} from './QuotationTypes'

interface QuotationComparisonProps {
  rfq: any;
  initialMaterialsData: any;
  supplierQuotations: any;
  countsData: any;
  quotationHeader: any;
  onUpdate: () => void; // Accept the onUpdate callback
}

type Material = {
  key: string;
  materialName: string;
  materialID: string;
  qty: number;
  uomName: string;
  awardedQty: any;
  deliveryDate: any;
  remainingQty: any;
  uom: string; // Added UOM (Unit of Measure) field
  targetPrice: number;
  quotationLineId: number;
};
//Allocation / Award Data
type SubmittedDataItem = {
  quotationLineId: number;
  materialID: string;
  materialName: string;
  supplierID: string;
  awardQty: string;
  reason: string;
};
const QuotationComparison: FC<QuotationComparisonProps> = ({
  rfq,
  initialMaterialsData,
  supplierQuotations,
  quotationHeader,
  countsData,
  onUpdate,
}) => {
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });
  const [countValues, setCountsData] = useState(countsData?.shortListCount);
  // const [initialMaterials, setInitialMaterials] = useState<Material[]>([initialMaterialsData]);
  const [materials, setMaterials] = useState<Material[]>(initialMaterialsData);
  const [inputValues, setInputValues] = useState<Record<string, string>>(
    initialMaterialsData.reduce(
      (acc: any, material: any) => ({
        ...acc,
        [material.key]: String(material.targetPrice),
      }),
      {}
    )
  );
  const [statusIndicatorDrawerVisible, setStatusIndicatorDrawerVisible] =
    useState(false);
  const [statusIndicatorRanges, setStatusIndicatorRanges] = useState<any>({});

  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]); // State to track selected materials
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false); // State for Select All checkbox
  const [allocationDrawerVisible, setAllocationDrawerVisible] = useState(false);
  const [submittedData, setSubmittedData]: any = useState<SubmittedDataItem[]>(
    []
  ); // This for Awared data from AllocationDrawer
  const [supplierDetailsDrawerVisible, setSupplierDetailsDrawerVisible] =
    useState(false);

  const [supplierDetailsData, setSupplierDetailsData] = useState<any>({});
  // const [supplierQuotations, setSupplierQuotations]: any = useState<SupplierQuotation[]>([]);
  const [purchaseHistoryParamsData, setPurchaseHistoryParamsData] =
    useState<any>({});
  const [purchaseHistoryVisible, setPurchaseHistoryVisible] = useState(false);
  const [purchaseSummaryParamsData, setPurchaseSummaryParamsData] =
    useState<any>({});
  const [purchaseSummaryVisible, setPurchaseSummaryVisible] = useState(false);
  const [form] = Form.useForm();


  const updateTargetPrice = (key: string, value: string) => {
    console.log("dddd", key, value);
    setInputValues((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));
  };

  const handleBlur = (key: string) => {
    const value = inputValues[key] == "" ? "0" : inputValues[key];
    setMaterials((prevMaterials) =>
      prevMaterials.map((material) =>
        material.key == key
          ? { ...material, targetPrice: Number(value) }
          : material
      )
    );
  };

  const getPriceColor = (unitPrice: number, targetPrice: number): string => {
    if (!targetPrice || targetPrice === 0) return "transparent";
    if (unitPrice <= targetPrice) return "transparent";
    const deviation = ((unitPrice - targetPrice) / targetPrice) * 100;
    const { slightlyHighRange, highRange, veryHighRange, outlierRange } =
      statusIndicatorRanges;
    if (outlierRange?.from !== undefined && deviation >= outlierRange.from)
      return "#ee545b"; // outlier
    if (veryHighRange?.from !== undefined && deviation >= veryHighRange.from)
      return "#faad14"; // very high
    if (highRange?.from !== undefined && deviation >= highRange.from)
      return "#f4e065"; // high
    if (slightlyHighRange?.from !== undefined && deviation >= slightlyHighRange.from)
      return "#f2d1ae"; // slightly high
    return "transparent";
  };

  const generateColumns = (
    updateTargetPrice: (key: string, value: string) => void,
    inputValues: Record<string, string>,
    handleBlur: (key: string) => void
  ): ColumnsType<any> => {
    const columns: ColumnsType<any> = [
      {
        title: (
          <Checkbox
            checked={isAllSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        ), // Select All
        dataIndex: "selection",
        key: "selection",
        fixed: "left",
        width: 50,
        render: (text: any, record: any) => {
          const isDisabled = record.remainingQty === 0;
          return (
            <Tooltip title={isDisabled ? "Remaining quantity is 0" : ""}>
              <Checkbox
                checked={selectedMaterials.includes(record.key)}
                disabled={isDisabled}
                onChange={(e) =>
                  handleMaterialSelection(record.key, e.target.checked)
                }
              />
            </Tooltip>
          );
        },
      },
      {
        title: "S.No",
        dataIndex: "key",
        key: "key",
        fixed: "left",
        width: 50,
      },
      {
        title: "Material Name",
        dataIndex: "materialName",
        key: "materialName",
        fixed: "left",
        width: 250,
      },
      {
        title: "Qty",
        dataIndex: "qty",
        key: "qty",
        fixed: "left",
        width: 110,
        render: (text: number, record: any) => (
          <>
            <span>
              {text} {record.uomName}
            </span>
            <Popover
              content={
                <div>
                  <p>Asked Quantity: {record.qty}</p>
                  <p>Reserved Quantity: {record.awardedQty}</p>
                  <p>Remaining Quantity: {record.remainingQty}</p>
                </div>
              }
              title="Tax Breakdown"
            >
              <InfoCircleOutlined style={{ marginLeft: 8 }} />
            </Popover>
          </>
        ),
      },
      {
        title: "Need By Date",
        dataIndex: "deliveryDate",
        key: "deliveryDate",
        fixed: "left",
        width: 100,
        render: (text: number, record: any) => (
          <>
            <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
          </>
        ),
      },
      {
        title: "Target Price (TP)",
        dataIndex: "targetPrice",
        key: "targetPrice",
        fixed: "left",
        width: 120,
        render: (text: number, record: any) => (
          <Input
            value={inputValues[record.key] || ""}
            onChange={(e) => updateTargetPrice(record.key, e.target.value)}
            onBlur={() => handleBlur(record.key)}
          />
        ),
      },
    ];
    const getSupplierInitials = (name: string) => {
      const words:any = name?.split(" ");
      if (words?.length > 1) {
        return `${words[0][0]}${words[1][0]}`.toUpperCase();
      }
      return name?.substring(0, 2).toUpperCase();
    };

    const colors = [
      "#173F5F",
      "#20639B",
      "#3CAEA3",
      "#F6D55C",
      "#ED553B",
      "#e81e63",
      "#03a9f4",
      "#cddc39",
      "#9c27b0",
      "#ffeb3b",
    ];

    supplierQuotations.forEach((supplier: any, index: any) => {
      const colorIndex = index % colors.length;
      columns.push({
        title: (
          <div className="supplier-name-wrapper">
            <div className="">
              <div
                onClick={() =>
                  handleSupplierClick(
                    supplier.supplierID,
                    supplier.supplierName
                  )
                }
                style={{ display: "flex", justifyContent: "center" }}
              >
                <Popover content={supplier.supplierName} placement="top">
                  <div
                    className="supplier-icon"
                    style={{ backgroundColor: colors[1], marginRight: 10 }}
                  >
                    {getSupplierInitials(supplier.supplierName)}
                  </div>
                </Popover>
                {supplier.score && supplier.score.rank ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewHistory({
                        supplierName: supplier.supplierName,
                        supplierId: Number(supplier.supplierID),
                        pageSize: 3,
                        quotationId: Number(rfq.quotationId),
                      });
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      color: "green",
                      marginLeft: 10,
                      cursor: "pointer",
                    }}
                  >
                    <TrophyOutlined style={{ marginRight: 5 }} />
                    <span>{supplier.score.rank}</span>
                  </div>
                ) : null}
              </div>
            </div>
            {/* <div>Price</div> */}
          </div>
        ),
        key: supplier.supplierID,
        dataIndex: `totalPrice_${supplier.supplierID}`,
        width: 150,
        render: (value: any, record: any) => {
          // console.log("value", value);
          const { unitPrice, totalPrice, supplyQty } = value || {
            unitPrice: 0,
            totalPrice: 0,
            supplyQty: 0,
          };
          const priceColor = getPriceColor(unitPrice, record.targetPrice);
          return (
            <div style={{ padding: 4, margin: 0, textAlign: "right" }}>
              <div>Supply Qty: {supplyQty}</div>
              <div
                style={{
                  backgroundColor: priceColor,
                  padding: priceColor !== "transparent" ? "2px 4px" : 0,
                  borderRadius: 3,
                  display: "inline-block",
                }}
              >
                Unit Price:{" "}
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  minimumFractionDigits: 0,
                }).format(unitPrice)}
              </div>
              <div>
                Total Price:{" "}
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  minimumFractionDigits: 0,
                }).format(totalPrice)}
              </div>
              <div>
                <a
                  className="link"
                  onClick={() => {
                    handleViewPurchaseSummary({
                      supplierName: supplier.supplierName,
                      supplierId: Number(supplier.supplierID),
                      pageSize: 3,
                      quotationId: Number(rfq.quotationId),
                      materialId: Number(value.materialID),
                      materialName: value.materialName,
                    });
                  }}
                >
                  View Purchase History
                </a>
              </div>
            </div>
          );
        },
      });
    });
    return columns;
  };

  const handleViewHistory = (supplierData: any) => {
    console.log("supplierData", supplierData);
    setPurchaseHistoryParamsData(supplierData);
    setPurchaseHistoryVisible(true);
  };

  const handleViewPurchaseSummary = (supplierData: any) => {
    setPurchaseSummaryParamsData(supplierData);
    setPurchaseSummaryVisible(true);
  };

  const closePurchaseHistory = () => {
    setPurchaseHistoryVisible(false);
    onUpdate(); // Refresh component after closing purchase history drawer
  };

  const closePurchaseSummary = () => {
    setPurchaseSummaryVisible(false);
    onUpdate();
  };

  const handleSupplierClick = async (
    supplierID: string,
    supplierName: string
  ) => {
    try {
      const { response, status } =
        await quotationApis.latestBySupplierQuotationId(
          rfq.quotationId,
          supplierID,
          0
        );
      if (status === true) {
        setSupplierDetailsData({ ...response.data, supplierName });
        setSupplierDetailsDrawerVisible(true);
        await fetchDMSData("sellerOrgLogo", supplierID);
        onUpdate(); // Refresh after fetching supplier details
      } else {
        console.log("response");
      }
    } catch {
      console.log("error");
    }
  };

  const fetchDMSData = async (type: string, id: any) => {
    try {
      const queryObj: any = {};
      switch (type) {
        case "buyerOrgLogo":
          queryObj["moduleFileType"] = "org logo";
          queryObj["module"] = "orgs";
          queryObj["orgId"] = id;
          break;
        case "sellerOrgLogo":
          queryObj["moduleFileType"] = "org logo";
          queryObj["module"] = "orgs";
          queryObj["orgId"] = id;
          break;
        case "termsAndConditions":
          queryObj["moduleFileType"] = "termsAndConditions";
          queryObj["module"] = "invoice";
          queryObj["invoiceNumber"] = id;
          break;
        default:
          return;
      }
      const _response = await miscService.getByQuery(queryObj);
      const { status, response: dmsData } = _response;
      if (status && dmsData?.length > 0) {
        const item = dmsData[0];
        switch (type) {
          case "buyerOrgLogo":
            let orgLogo = item?.filePath;
            setSupplierDetailsData((pre: any) => ({
              ...pre,
              buyerOrgLogo: orgLogo,
            }));
            break;
          case "sellerOrgLogo":
            let sellerOrgLogo = item?.filePath;
            setSupplierDetailsData((pre: any) => ({
              ...pre,
              sellerOrgLogo: sellerOrgLogo,
            }));
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const generateDataSource = (materials: Material[]): Record<any, any>[] => {
    return materials.map((material) => {
      const data: Record<string, any> = {
        key: material.key,
        materialName: material.materialName,
        qty: material.qty,
        uom: material.uom,
        uomName: material.uomName,
        deliveryDate: material.deliveryDate,
        awardedQty: material.awardedQty,
        remainingQty: material.remainingQty,
        targetPrice: material.targetPrice,
      };
      supplierQuotations.forEach((supplier: any) => {
        const quotation = supplier.quotation.find(
          (q: any) => q.quotationLineId === material.quotationLineId
        );
        if (quotation) {
          data[`totalPrice_${supplier.supplierID}`] = {
            quotationLineId: material.quotationLineId,
            supplyQty: quotation.supplyQty,
            materialID: material.materialID,
            materialName: material.materialName,
            unitPrice: quotation.unitPrice,
            totalPrice: quotation.unitPrice * quotation.supplyQty,
          };
        }
      });
      return data;
    });
  };

  const columns: any = useMemo(
    () => generateColumns(updateTargetPrice, inputValues, handleBlur),
    [inputValues, statusIndicatorRanges, supplierQuotations, isAllSelected, selectedMaterials]
  );
  const dataSource = generateDataSource(materials);

  const showStatusIndicatorDrawer = () => {
    setStatusIndicatorDrawerVisible(true);
  };

  const closeStatusIndicatorDrawer = () => {
    setStatusIndicatorDrawerVisible(false);
    onUpdate(); // Refresh component after closing status indicator drawer
  };

  const closeSupplierDrawer = () => {
    setSupplierDetailsDrawerVisible(false);
    onUpdate(); // Refresh component after closing supplier drawer
  };

  const handleStatusIndicatorSubmit = (values: any) => {
    console.log("Submitted Values: ", values);
    setStatusIndicatorRanges(values);
    message.success("Status indicators have been set successfully.");
    onUpdate(); // Refresh component after status indicator update
  };
  // State to control the ScenarioBuilderDrawer visibility
  const [scenarioBuilderVisible, setScenarioBuilderVisible] = useState(false);
  const [supplierQuoteVisible, setSupplierQuoteVisible] = useState(false);
  // Handler to open the ScenarioBuilderDrawer
  const openScenarioBuilder = (e: any) => {
    e.preventDefault();
    getScenarioBuilder();
    setScenarioBuilderVisible(true);
  };

  // Handler to close the ScenarioBuilderDrawer
  const closeScenarioBuilder = () => {
    setScenarioBuilderVisible(false);
    onUpdate(); // Refresh component after closing scenario builder drawer
  };

  const closeSupplierQuoteDrawer = () => {
    setSupplierQuoteVisible(false);
    onUpdate(); // Refresh component after closing supplier quote drawer
  };

  // Handler to handle the form submission from ScenarioBuilderDrawer
  const handleScenarioSubmit = (values: any) => {
    console.log("Scenario Values: ", values, rfq.quotationId);
    const formatData = {
      quotationId: rfq.quotationId,
      materialCoverage: values.materialCoverage,
      volumeCoverage: values.volumeCoverage,
      creditCoverage: values.longCreditDays,
      quotationValueCoverage: values.quotationValue,
      onTimeDeliveryCoverage: values.onTimeDelivery,
      delayPercentage: values.delayPercentage,
      credit30Days: values.credit30Days,
    };
    console.log("formatData", formatData);
    addScenarioBuilder(formatData);
  };

  const handleSupplierQuoteSubmit = (values: any) => {
    console.log("vlaue", values);
    closeSupplierQuoteDrawer(); // This will call onUpdate()
  };

  const addScenarioBuilder = async (data: any) => {
    try {
      const { response, status } = await quotationApis.addScenarioBuilder(data);
      if (status) {
        message.success("Scenario has been added successfully.");
        closeScenarioBuilder(); // This will call onUpdate()
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleMaterialSelection = (key: string, isSelected: boolean) => {
    console.log("Selected material key:", key, "isSelected:", isSelected);
    if (isSelected) {
      setSelectedMaterials((prev) => {
        console.log("Prev selected materials:", prev);
        return [...prev, key];
      });
    } else {
      setSelectedMaterials((prev) => {
        console.log("Prev selected materials:", prev);
        return prev.filter((item) => item !== key);
      });
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    console.log("Select all isSelected:", isSelected);
    if (isSelected) {
      // Only select materials where remainingQty is not 0
      const selectableMaterials = materials
        .filter((material) => material.remainingQty !== 0) // Exclude materials with remainingQty === 0
        .map((material) => material.key);
      setSelectedMaterials(selectableMaterials);
    } else {
      setSelectedMaterials([]); // Clear selection if not selected
    }
    setIsAllSelected(isSelected);
  };

  const openAllocationDrawer = (e: any) => {
    e.preventDefault();
    if (selectedMaterials.length > 0) {
      setAllocationDrawerVisible(true);
    } else {
      message.warning("Please select at least one material to allocate.");
    }
  };

  const closeAllocationDrawer = () => {
    setAllocationDrawerVisible(false);
    onUpdate(); // Refresh component after closing allocation drawer
  };
  const [allocationStatusColor, setAllocationStatusColor] = useState("red");
  const [allocationStatus, setAllocationStatus] = useState("Not Awarded");

  const handleAllocationSubmit = (values: SubmittedDataItem[]) => {
    setSubmittedData((prev: any) => {
      const updatedData = [...prev, ...values];
      console.log("updatedData ", JSON.stringify(updatedData));
      const totalRequired = materials.reduce(
        (acc, material) => acc + material.qty,
        0
      );
      const totalAwarded = materials.reduce((acc, material) => {
        // Filter the updatedData for the current material
        const total = updatedData
          .filter((item) => item.materialID === material.materialID)
          .reduce((sum, item) => sum + parseFloat(item.awardQty), 0);

        // Add the total awarded quantity for this material to the accumulator
        return acc + total;
      }, 0);

      console.log("totalAwarded ", totalAwarded);
      console.log("materials.length ", totalRequired);

      const allocationStatus =
        totalAwarded === 0
          ? "Not Awarded"
          : totalAwarded === totalRequired
          ? "Fully Awarded"
          : "Partially Awarded";

      const allocationStatusColor =
        allocationStatus === "Fully Awarded"
          ? "green"
          : allocationStatus === "Partially Awarded"
          ? "orange"
          : "red";
      setAllocationStatus(allocationStatus);
      setAllocationStatusColor(allocationStatusColor);

      return updatedData;
    });
    setSelectedMaterials([]);
    form.resetFields();

    // message.success("Successfully awarded!");
    createAllocationSubmit(values);
  };

  const createAllocationSubmit = async (values: any) => {
    console.log("values", values);
    try {
      const { response, status } = await quotationApis.shortListSuppliers(
        rfq.quotationId,
        {
          list: values,
        }
      );
      if (status) {
        setAllocationDrawerVisible(false);
        message.success("Successfully Allocated!");
        onUpdate();
      } else {
        console.log("status", message);
        Swal.fire("Failed!", "Something went wrong", "error");
      }
    } catch (error: any) {
      console.log("error", error);
      // message.error(error.message);
    }
  };

  useEffect(() => {
    // Update local state with the new props when they change
    setCountsData(countsData?.shortListCount);
    setMaterials(initialMaterialsData);
    setInputValues(
      initialMaterialsData.reduce(
        (acc: any, material: any) => ({
          ...acc,
          [material.key]: String(material.targetPrice),
        }),
        {}
      )
    );
    // Reset selections when materials data changes
    setSelectedMaterials([]);
    setIsAllSelected(false);
  }, [initialMaterialsData, countsData, supplierQuotations]);

  useEffect(() => {
    console.log("Updated supplier quotations: ", supplierQuotations);
  }, [supplierQuotations]);

  useEffect(() => {
    console.log("submittedData updated:", submittedData);
  }, [submittedData, allocationStatus, allocationStatusColor]);

  useEffect(() => {
    if (accessibility) {
      try {
        const rfqAccess = accessibility["Quotations"];
        if (rfqAccess) {
          const { add, view, edit, approve, reject, cancel } = rfqAccess;
          setAllowedActions({ add, view, edit, approve, reject, cancel });
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [shortlistedData, setShortlistedData] = useState([]);

  const showDrawer = async () => {
    if (countsData?.shortListCount === 0) {
      message.warning("There are no shortlisted suppliers.");
      return;
    }
    setDrawerVisible(true);
    // alert("Hi");
    // const { response, status } = await quotationApis.getShortListed(rfq.quotationId);
    // if (status) {
    //   console.log("log:::LIST", response)
    //   // console.log("map list ", submittedData, supplierQuotations)
    //   const data: ShortlistedSupplier[] = response.data.suppliers;
    //   console.log(data);
    //   setShortlistedData(data as any);
    //   setDrawerVisible(true);
    // }
  };

  const [scenarioBuilderData, setScenarioBuilderData] = useState([]);

  const [supplierQuoteData, setSupplierQuoteData]: any = useState<any>();

  const getScenarioBuilder = async () => {
    try {
      const { response, status } = await quotationApis.getScenarioBuilder(
        rfq.quotationId
      );
      if (status) {
        setScenarioBuilderData(response.data);
        console.log("setScenarioBuilderData", response.data);
      }
    } catch (error) {}
  };

  const saveTargetPriceValue = async () => {
    const targets = materials.map((key) => {
      return {
        quotationLineId: key.quotationLineId,
        targetPrice: key.targetPrice,
      };
    });

    console.log("target", targets);
    if (targets.length !== 0) {
      saveAPICall(targets);
    } else if (targets.length === 0) {
      message.warning("Target Not Entred.");
    }
  };

  const openSupplierQuoteDrawer = () => {
    const data: any = {
      materials: materials,
      quotationHeader: quotationHeader,
    };
    setSupplierQuoteData(data);
    setSupplierQuoteVisible(true);
  };

  const saveAPICall = async (targets: any) => {
    console.log("ddd");
    try {
      const { response, status } = await quotationApis.addTargetPrice({
        targets: targets,
      });
      if (status) {
        Swal.fire("Saved", "Target Price is Set", "success");
        onUpdate();
      }
    } catch (error) {
      Swal.fire("Failed!", "Something went wrong", "error");
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    onUpdate();
  };
  return (
    <div className="table-container">
      <Row>
        <Col lg="12">
          <Card
            className="card-bordered"
            style={{ minHeight: "500px", overflowY: "auto" }}
          >
            <div className="card-inner">
              <div className="status-indicator-wrapper">
                <Space>
                  <div className="scenarioBuilderButton">
                    <a
                      href="#"
                      className="btn btn-dim btn-outline-light"
                      onClick={openScenarioBuilder}
                    >
                      <BarChartOutlined /> &nbsp; Scenario Builder
                    </a>
                  </div>
                </Space>
                <Space>
                  <div className="status-indicator slightly-high">
                    <span
                      className="color-box"
                      style={{ backgroundColor: "#f2d1ae" }}
                    ></span>
                    Slightly High
                  </div>
                  <div className="status-indicator high">
                    <span
                      className="color-box"
                      style={{ backgroundColor: "#f4e065" }}
                    ></span>
                    High
                  </div>
                  <div className="status-indicator very-high">
                    <span
                      className="color-box"
                      style={{ backgroundColor: "#faad14" }}
                    ></span>
                    Very High
                  </div>
                  <div className="status-indicator outlier">
                    <span
                      className="color-box"
                      style={{ backgroundColor: "#ee545b" }}
                    ></span>
                    Outlier
                  </div>
                  <Tooltip title="Edit">
                    <EditOutlined
                      className="edit-icon"
                      onClick={showStatusIndicatorDrawer}
                    />
                  </Tooltip>
                </Space>
                <div>
                  <a onClick={saveTargetPriceValue}>
                    <span>
                      <Tooltip title="Save Target Price">
                        <div style={{ marginLeft: "10px", marginTop: "-2px" }}>
                          <a>Save Target Price </a>
                        </div>
                      </Tooltip>
                    </span>
                  </a>
                </div>
                {allowedActions?.add && !(quotationHeader?.deadlineType === "HARD" && dayjs().isAfter(dayjs.utc(quotationHeader?.lastSubmissionDate).local())) ? (<div>
                  <a onClick={openSupplierQuoteDrawer}>
                    <span>
                      <Tooltip title="Supplier Quote">
                        <div style={{ marginLeft: "10px", marginTop: "-2px" }}>
                          Set Supplier Quote{" "}
                        </div>
                      </Tooltip>
                    </span>
                  </a>
                </div>) : (<></>)}
                <div>
                  <a onClick={openAllocationDrawer}>
                    Allocate / Shortlist{" "}
                    {/* <span style={{ color: allocationStatusColor }}>
                      {allocationStatus}
                    </span> */}
                  </a>
                </div>
                <div>
                  <Space>
                    <Tooltip title="Click to view shortlisted suppliers">
                      <a onClick={showDrawer}>
                        Shortlisted Suppliers: {countValues}
                        {/* {
                          new Set(submittedData.map((item: any) => item.supplierID))
                            .size
                        } */}
                      </a>
                    </Tooltip>
                  </Space>
                </div>
              </div>
              <div id="comparison-table">
                <Form form={form} component={false}>
                  <Table
                    dataSource={dataSource}
                    columns={columns.map((column: any) => {
                      let head = quotationHeader?.deliveryDateType;
                      // console.log("colll", head, column.dataIndex)
                      // if (head !== "ORDER_LEVEL" && column.dataIndex == "deliveryDate") {
                      //   // console.log("111111")
                      //   return {
                      //     ...column,
                      //     hidden: true,
                      //   };
                      // }
                      return column;
                    })}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                    size="small"
                    bordered
                    style={{ paddingTop: 10 }}
                    summary={() => (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell
                            index={0}
                            colSpan={6}
                            align="right"
                            className="summary-header-cell"
                          >
                            <span className="summary-header">Sub Total</span>
                          </Table.Summary.Cell>
                          {supplierQuotations.map(
                            (supplier: any, index: any) => (
                              <Table.Summary.Cell
                                key={supplier.supplierID}
                                index={index + 5}
                                align="right"
                                className="summary-value-cell"
                              >
                                {new Intl.NumberFormat("en-IN", {
                                  style: "currency",
                                  currency: "INR",
                                  minimumFractionDigits: 0,
                                }).format(supplier.subTotal)}
                              </Table.Summary.Cell>
                            )
                          )}
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell
                            index={0}
                            colSpan={6}
                            align="right"
                            className="summary-header-cell"
                          >
                            <span className="summary-header">Total Taxes</span>
                          </Table.Summary.Cell>
                          {supplierQuotations.map(
                            (supplier: any, index: any) => (
                              <Table.Summary.Cell
                                key={supplier.supplierID}
                                index={index + 5}
                                align="right"
                                className="summary-value-cell"
                              >
                                {new Intl.NumberFormat("en-IN", {
                                  style: "currency",
                                  currency: "INR",
                                  minimumFractionDigits: 0,
                                }).format(supplier.totalTaxes)}
                              </Table.Summary.Cell>
                            )
                          )}
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell
                            index={0}
                            colSpan={6}
                            align="right"
                            className="summary-header-cell"
                          >
                            <span className="summary-header">
                              Payment Terms
                            </span>
                          </Table.Summary.Cell>
                          {supplierQuotations.map(
                            (supplier: any, index: any) => (
                              <Table.Summary.Cell
                                key={supplier.supplierID}
                                index={index + 5}
                                align="right"
                                className="summary-value-cell"
                              >
                                {supplier.paymentTerms} &nbsp;
                                {supplier.paymentTerms === "Credit" && (
                                  <>( {supplier.paymentDays} days )</>
                                )}
                                {supplier.paymentTerms === "Advance" && (
                                  <>( {supplier.paymentPercentage} % )</>
                                )}
                              </Table.Summary.Cell>
                            )
                          )}
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell
                            index={0}
                            colSpan={6}
                            align="right"
                            className="summary-header-cell"
                          >
                            <span className="summary-header">
                              Delivery Lead Days
                            </span>
                          </Table.Summary.Cell>
                          {supplierQuotations.map(
                            (supplier: any, index: any) => (
                              <Table.Summary.Cell
                                key={supplier.supplierID}
                                index={index + 5}
                                align="right"
                                className="summary-value-cell"
                              >
                                {supplier.leadTime}{" "}
                                {supplier.leadTime && "days"}
                              </Table.Summary.Cell>
                            )
                          )}
                        </Table.Summary.Row>

                        <Table.Summary.Row>
                          <Table.Summary.Cell
                            index={0}
                            colSpan={6}
                            align="right"
                            className="summary-header-cell"
                          >
                            <span className="summary-header">
                              Additional Charges
                            </span>
                          </Table.Summary.Cell>
                          {supplierQuotations.map(
                            (supplier: any, index: any) => (
                              <Table.Summary.Cell
                                key={supplier.supplierID}
                                index={index + 5}
                                align="right"
                                className="summary-value-cell"
                              >
                                {new Intl.NumberFormat("en-IN", {
                                  style: "currency",
                                  currency: "INR",
                                  minimumFractionDigits: 0,
                                }).format(supplier.totalAdditionalCharges)}
                              </Table.Summary.Cell>
                            )
                          )}
                        </Table.Summary.Row>

                        <Table.Summary.Row>
                          <Table.Summary.Cell
                            index={0}
                            colSpan={6}
                            align="right"
                            className="summary-header-cell"
                          >
                            <span className="summary-header">Grand Total</span>
                          </Table.Summary.Cell>
                          {supplierQuotations.map(
                            (supplier: any, index: any) => (
                              <Table.Summary.Cell
                                key={supplier.supplierID}
                                index={index + 5}
                                align="right"
                                className="summary-value-cell"
                              >
                                {new Intl.NumberFormat("en-IN", {
                                  style: "currency",
                                  currency: "INR",
                                  minimumFractionDigits: 0,
                                }).format(supplier.grandTotal)}
                              </Table.Summary.Cell>
                            )
                          )}
                        </Table.Summary.Row>
                      </>
                    )}
                  />
                </Form>
              </div>
            </div>
            <StatusIndicatorDrawer
              visible={statusIndicatorDrawerVisible}
              onClose={closeStatusIndicatorDrawer}
              onSubmit={handleStatusIndicatorSubmit} // Handle submission from the child component
            />
            <ScenarioBuilderDrawer
              scenarioBuilderData={scenarioBuilderData}
              visible={scenarioBuilderVisible}
              onClose={closeScenarioBuilder}
              onSubmit={handleScenarioSubmit}
            />
            <PurchaseHistoryDrawer
              paramsData={purchaseHistoryParamsData}
              visible={purchaseHistoryVisible}
              onClose={closePurchaseHistory}
            />
            <PurchaseSummaryDrawer
              paramsData={purchaseSummaryParamsData}
              visible={purchaseSummaryVisible}
              onClose={closePurchaseSummary}
            />
            <AddSupplierQuoteDrawer
              supplierQuoteData={supplierQuoteData}
              quotationId={rfq.quotationId}
              rfqId={quotationHeader.rfqId || rfq.rfqId}
              visible={supplierQuoteVisible}
              onClose={closeSupplierQuoteDrawer}
              onSubmit={handleSupplierQuoteSubmit}
            />
            {allocationDrawerVisible && (
              <AllocationDrawer
                visible={allocationDrawerVisible}
                onClose={closeAllocationDrawer}
                materials={materials.filter((material) =>
                  selectedMaterials.includes(material.key)
                )}
                supplierQuotations={supplierQuotations}
                submittedData={submittedData}
                onSubmit={handleAllocationSubmit} // Ensure this is correctly passed
              />
            )}
            {drawerVisible && (
              <ShortlistedSuppliersDrawer
                visible={drawerVisible}
                onClose={closeDrawer}
                rfqId={rfq.quotationId}
                siteName={rfq.siteName}
                siteId={rfq.siteId}
                quotation={quotationHeader.rfqId || rfq.rfqId}
              />
            )}
            {supplierDetailsDrawerVisible && (
              <SupplierDetailsDrawer
                visible={supplierDetailsDrawerVisible}
                onClose={closeSupplierDrawer}
                rfqId={rfq.quotationId}
                siteName={rfq.siteName}
                dataDrawer={supplierDetailsData}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default QuotationComparison;
